import { useCallback, useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import * as s from './PanelModalShell.css';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

// shared/ui/atoms/Modal 과 같은 카운터 계열 — 다중 모달이 떠도 마지막 close 에서만 body 복원.
let modalCount = 0;

export interface PanelModalShellProps {
  /** 스크린리더가 읽을 다이얼로그 이름. */
  ariaLabel: string;
  onClose: () => void;
  /** 패널 본체. 자체 chrome(헤더·푸터)을 갖춘 self-contained 패널을 넣는다. */
  children: ReactNode;
}

/**
 * 자체 chrome 을 갖춘 패널을 모달로 띄우는 껍데기.
 *
 * `shared/ui/atoms/Modal` 은 헤더·닫기 버튼 같은 chrome 을 직접 그리므로, QuestCreatePanel
 * 처럼 이미 chrome 을 가진 패널을 감싸면 이중으로 겹친다. 그래서 이 껍데기는 모달 **행동**만
 * 진다 — portal, scroll-lock, focus trap, focus restore, ESC·배경 클릭 닫기, role=dialog.
 *
 * 같은 코드가 퀘스트 생성·수정·제보 전환 세 호스트에 복제돼 있던 것을 한곳으로 모은 것이다.
 * 페이지끼리는 서로 import 할 수 없어(FSD 동일 레이어) 공유 지점이 shared 여야 한다.
 */
export function PanelModalShell({ ariaLabel, onClose, children }: PanelModalShellProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    modalCount++;
    document.body.style.overflow = 'hidden';
    document.body.dataset.modalOpen = 'true';
    return () => {
      modalCount = Math.max(0, modalCount - 1);
      if (modalCount === 0) {
        document.body.style.overflow = '';
        delete document.body.dataset.modalOpen;
      }
    };
  }, []);

  useEffect(() => {
    previousFocusRef.current = document.activeElement as HTMLElement | null;
    const raf = requestAnimationFrame(() => {
      const panel = panelRef.current;
      if (!panel) return;
      const first = panel.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      (first ?? panel).focus();
    });
    return () => {
      cancelAnimationFrame(raf);
      previousFocusRef.current?.focus();
    };
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusable = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first && last) {
          e.preventDefault();
          last.focus();
        }
      } else if (document.activeElement === last && first) {
        e.preventDefault();
        first.focus();
      }
    },
    [onClose]
  );

  return createPortal(
    // 오버레이의 클릭·키다운은 "배경 클릭으로 닫기"와 focus trap 이라 의도된 패턴이다.
    // role=dialog 가 붙어 있어 실제 상호작용 대상은 안쪽 패널이다.
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- 배경 클릭 닫기와 포커스 가두기·Esc 를 대화상자 바깥 상자에서 받는다. 조작 대상은 안쪽 패널이다
    <div
      className={s.overlay}
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={handleKeyDown}
    >
      <div ref={panelRef} className={s.panelBox} tabIndex={-1}>
        {children}
      </div>
    </div>,
    document.body
  );
}
